import React, { useRef, useState } from 'react';
import {
  Dialog, DialogContent, DialogActions, Box, Typography, Button,
  IconButton, Tooltip, ToggleButtonGroup, ToggleButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PrintIcon from '@mui/icons-material/Print';
import DownloadIcon from '@mui/icons-material/Download';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import BadgeIcon from '@mui/icons-material/Badge';
import { QRCodeSVG } from 'qrcode.react';
import logoCapellaniasBadge from '../assets/logocapellanias.png';
import carnetCapellaniasBase from '../assets/carnetCapellaniasBaseClean.jpeg';
import escudoMask from '../assets/escudoMask.png';

// ─────────────────────────────────────────────────────────────────────
// VERSIÓN CLARA (la original) — usada por FENAGRO y por Capellanías v1
// Para Capellanías el cuadradito superior muestra el badge dorado en
// lugar de la "✝" anterior.
// ─────────────────────────────────────────────────────────────────────
export function CarnetCard({ carnet, fotoUrl, cardRef }) {
  if (!carnet) return null;

  const siglas = carnet.cooperativa?.siglas;
  const esCapellanes = siglas === 'CAPELLANES';

  const palette = esCapellanes ? {
    header: '#1e3a5f', accent: '#c8a84b', headerLight: '#b3c7e1',
    chipBg: '#e6edf6', chipTxt: '#1e3a5f',
    photoBg: '#eef2f7', footerBg: '#f1f4f8',
    label: 'CARNET MINISTERIAL',
    coopHeader: 'Alianza solidaria de capellanes',
    coopSub: 'Ministerio de capellanía · Colombia',
  } : {
    header: '#1a4d2e', accent: '#c8a84b', headerLight: '#c8e6c9',
    chipBg: '#e8f5e9', chipTxt: '#1a4d2e',
    photoBg: '#f0ebe0', footerBg: '#f8f5ef',
    label: 'CARNET DE AFILIADO',
    coopHeader: 'FENAGRO APL',
    coopSub: 'Federación agroindustrial · Colombia',
  };

  const fmtFecha = (d) =>
    d ? new Date(d).toLocaleDateString('es-CO', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

  const urlValidacion = carnet.urlValidacion
    || (esCapellanes
        ? `https://solidarityallianceofchaplains.org/v/${carnet.codigo}`
        : `https://fenagro-oficial.org/v/${carnet.codigo}`);

  const t = carnet.titular || {};
  const initials = ((t.nombre?.[0] || '') + (t.apellido?.[0] || '')).toUpperCase();
  const photo = fotoUrl || t.fotoUrl || null;

  return (
    <Box
      ref={cardRef}
      sx={{
        width: 320, background: '#ffffff', borderRadius: '14px',
        overflow: 'hidden', boxShadow: '0 8px 28px rgba(0,0,0,0.18)',
        color: '#1a1a1a',
      }}
    >
      {/* Lanyard slot */}
      <Box sx={{ background: palette.header, py: 1, display: 'flex', justifyContent: 'center' }}>
        <Box sx={{ width: 38, height: 5, background: 'rgba(0,0,0,0.25)', borderRadius: 3 }}/>
      </Box>

      {/* Header */}
      <Box sx={{ background: palette.header, px: 2, pt: 0.5, pb: 1.8, color: '#fff' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
          {esCapellanes ? (
            // Badge dorado (logo1.png) reemplaza la cruz anterior
            <Box sx={{
              width: 40, height: 40,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              flexShrink: 0,
            }}>
              <img src={logoCapellaniasBadge} alt="Capellanías"
                style={{ width: '100%', height: '100%', objectFit: 'contain' }}/>
            </Box>
          ) : (
            <Box sx={{
              width: 32, height: 32, background: palette.accent, borderRadius: 1,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: palette.header, fontWeight: 700, fontSize: '1.1rem',
            }}>F</Box>
          )}
          <Box sx={{ lineHeight: 1.2 }}>
            <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, letterSpacing: '0.01em' }}>
              {palette.coopHeader}
            </Typography>
            <Typography sx={{ fontSize: '0.66rem', color: palette.headerLight, letterSpacing: '0.04em' }}>
              {palette.coopSub}
            </Typography>
          </Box>
        </Box>
      </Box>

      <Box sx={{ height: 4, background: palette.accent }}/>

      {/* Body */}
      <Box sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', gap: 1.5, mb: 1.7 }}>
          {photo ? (
            <Box sx={{
              width: 78, height: 98, borderRadius: 1, overflow: 'hidden',
              border: '0.5px solid rgba(0,0,0,0.12)', flexShrink: 0, background: palette.photoBg,
            }}>
              <img src={photo} alt=""
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}/>
            </Box>
          ) : (
            <Box sx={{
              width: 78, height: 98, borderRadius: 1, background: palette.photoBg,
              border: '0.5px solid rgba(0,0,0,0.12)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              flexShrink: 0, fontSize: '1.6rem', fontWeight: 700, color: '#888780',
            }}>
              {initials || <PersonOutlineIcon sx={{ fontSize: 38 }}/>}
            </Box>
          )}

          <Box sx={{ flex: 1, minWidth: 0, pt: 0.5 }}>
            <Typography sx={{ fontSize: '0.66rem', color: '#888780', letterSpacing: '0.1em', mb: 0.4 }}>
              {palette.label}
            </Typography>
            <Typography sx={{ fontSize: '0.95rem', fontWeight: 700, color: '#1a1a1a', lineHeight: 1.2, mb: 0.6 }}>
              {t.nombre} {t.apellido}
            </Typography>
            <Box sx={{
              display: 'inline-block', px: 1, py: 0.3,
              background: palette.chipBg, color: palette.chipTxt,
              fontSize: '0.7rem', fontWeight: 600, borderRadius: 0.5,
            }}>
              {esCapellanes ? 'Capellán institucional' : 'Afiliado'}
            </Box>
          </Box>
        </Box>

        <Box sx={{ borderTop: '0.5px solid rgba(0,0,0,0.1)', pt: 1.2 }}>
          <Row label="Documento" value={`${t.tipoDocumento || ''} ${t.documento || ''}`}/>

          {esCapellanes && carnet.numeroRegistro && (
            <Box sx={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              p: 0.8, my: 0.6,
              background: '#fdfaf0', border: `0.5px solid ${palette.accent}`,
              borderRadius: 0.7,
            }}>
              <Typography sx={{ fontSize: '0.68rem', color: '#9c7e32', fontWeight: 600, letterSpacing: '0.06em' }}>
                N° REGISTRO
              </Typography>
              <Typography sx={{ fontSize: '0.78rem', color: palette.header, fontWeight: 700, fontFamily: 'monospace' }}>
                {carnet.numeroRegistro}
              </Typography>
            </Box>
          )}

          <Row label="Expedición" value={fmtFecha(carnet.fechaExpedicion)}/>
          <Row label="Vigencia" value={fmtFecha(carnet.fechaVigencia)} valueColor={palette.header} valueBold/>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 1.5, pt: 1.3, borderTop: '0.5px solid rgba(0,0,0,0.1)' }}>
          <Box sx={{ background: '#fff', p: 0.4, border: '0.5px solid rgba(0,0,0,0.08)', borderRadius: 0.5, lineHeight: 0 }}>
            <QRCodeSVG value={urlValidacion} size={66} level="M" includeMargin={false}/>
          </Box>
          <Box sx={{ minWidth: 0, fontSize: '0.7rem', color: '#4a5568', lineHeight: 1.45 }}>
            <Typography sx={{ color: '#888780', letterSpacing: '0.06em', fontSize: '0.62rem', mb: 0.3 }}>
              VERIFICACIÓN
            </Typography>
            <Typography sx={{ color: palette.header, fontWeight: 600, fontFamily: 'monospace', fontSize: '0.68rem', wordBreak: 'break-all' }}>
              {urlValidacion.replace(/^https?:\/\//, '').replace(/\/v\/.*$/, '/v/...')}
            </Typography>
            <Typography sx={{ color: '#1a1a1a', fontFamily: 'monospace', fontSize: '0.68rem' }}>
              {carnet.codigo}
            </Typography>
          </Box>
        </Box>
      </Box>

      <Box sx={{
        background: palette.footerBg, py: 0.8, px: 2,
        fontSize: '0.66rem', color: '#888780', textAlign: 'center',
        letterSpacing: '0.04em', borderTop: '0.5px solid rgba(0,0,0,0.06)',
      }}>
        Documento oficial · Verifique escaneando el código QR
      </Box>
    </Box>
  );
}

function Row({ label, value, valueColor, valueBold }) {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.4 }}>
      <Typography sx={{ fontSize: '0.7rem', color: '#888780', letterSpacing: '0.04em' }}>{label}</Typography>
      <Typography sx={{
        fontSize: '0.74rem',
        color: valueColor || '#1a1a1a',
        fontWeight: valueBold ? 700 : 500,
        fontFamily: 'monospace',
      }}>
        {value}
      </Typography>
    </Box>
  );
}

// ─────────────────────────────────────────────────────────────────────
// VERSIÓN OSCURA — para Capellanías v2, inspirada en carnetCapellanias.jpeg
// Fondo negro con acentos verde neón. Layout vertical tipo lanyard.
// ─────────────────────────────────────────────────────────────────────
export function CarnetCardOscuro({ carnet, fotoUrl, cardRef }) {
  if (!carnet) return null;

  const t = carnet.titular || {};
  const photo = fotoUrl || t.fotoUrl || null;
  const initials = ((t.nombre?.[0] || '') + (t.apellido?.[0] || '')).toUpperCase();

  const urlValidacion = carnet.urlValidacion
    || `https://solidarityallianceofchaplains.org/v/${carnet.codigo}`;
  const urlCorta = urlValidacion.replace(/^https?:\/\//, '').replace(/\/v\/.*$/, '/v/...');

  const fmtVigencia = carnet.estado === 'ACTIVO' ? 'AFILIADO ACTIVO' : carnet.estado;

  const neonGreen = '#7BFF1A';
  const neonGreenDim = '#5BC410';

  return (
    <Box
      ref={cardRef}
      sx={{
        width: 360,
        background: '#0a0a0a',
        borderRadius: '18px',
        overflow: 'hidden',
        position: 'relative',
        boxShadow: '0 12px 40px rgba(0,0,0,0.5), 0 0 0 1px rgba(123,255,26,0.15)',
        color: '#fff',
        fontFamily: '"Outfit", "Inter", sans-serif',
      }}
    >
      {/* Decoración esquinas verde neón */}
      <Box sx={{
        position: 'absolute', top: 0, right: 0, width: '50%', height: 70,
        background: `linear-gradient(135deg, transparent 40%, ${neonGreen}40 50%, ${neonGreenDim}80 80%, ${neonGreen})`,
        clipPath: 'polygon(20% 0, 100% 0, 100% 100%, 50% 60%)',
        opacity: 0.85,
      }}/>
      <Box sx={{
        position: 'absolute', bottom: 0, left: 0, width: 100, height: 120,
        background: `linear-gradient(45deg, ${neonGreen} 0%, transparent 70%)`,
        clipPath: 'polygon(0 30%, 0 100%, 70% 100%)',
        opacity: 0.4,
      }}/>

      {/* Lanyard slot */}
      <Box sx={{ pt: 1.5, display: 'flex', justifyContent: 'center', position: 'relative', zIndex: 2 }}>
        <Box sx={{ width: 44, height: 6, background: '#333', borderRadius: 3 }}/>
      </Box>

      {/* Header con badge + título */}
      <Box sx={{ display: 'flex', gap: 1.5, px: 2.5, pt: 2, pb: 1.5, position: 'relative', zIndex: 2 }}>
        <Box sx={{
          width: 78, height: 78, flexShrink: 0,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          background: 'rgba(255,255,255,0.04)',
          borderRadius: '50%', border: `1.5px solid ${neonGreen}`,
          boxShadow: `0 0 16px ${neonGreen}50`,
        }}>
          <img src={logoCapellaniasBadge} alt="Alianza"
            style={{ width: '85%', height: '85%', objectFit: 'contain' }}/>
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: '0.7rem', color: '#aaa', letterSpacing: '0.05em', mb: 0.2 }}>
            CARNE DE
          </Typography>
          <Typography sx={{
            fontSize: '1.4rem', fontWeight: 900, color: neonGreen, lineHeight: 1, letterSpacing: '0.02em',
            textShadow: `0 0 8px ${neonGreen}80`,
          }}>
            CAPELLANÍAS
          </Typography>
          <Typography sx={{ fontSize: '1.05rem', fontWeight: 800, color: '#fff', lineHeight: 1.1, mt: 0.2 }}>
            CASTRENSE
          </Typography>
          <Box sx={{
            display: 'inline-block', mt: 0.6, px: 1.2, py: 0.25,
            border: `1px solid ${neonGreen}`,
            borderRadius: 999,
            background: 'rgba(123,255,26,0.08)',
          }}>
            <Typography sx={{
              fontSize: '0.58rem', color: neonGreen, fontWeight: 700, letterSpacing: '0.1em',
            }}>
              RED SOCIO EMPRESARIAL
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Body: foto + datos */}
      <Box sx={{ display: 'flex', gap: 1.5, px: 2.5, py: 1.5, position: 'relative', zIndex: 2 }}>
        {/* Foto en hexágono/escudo */}
        <Box sx={{
          width: 110, height: 130, flexShrink: 0,
          background: '#fff',
          clipPath: 'polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          boxShadow: `0 0 14px ${neonGreen}60`,
          position: 'relative',
        }}>
          {photo ? (
            <img src={photo} alt=""
              style={{ width: '92%', height: '92%', objectFit: 'cover',
                       clipPath: 'polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%)' }}/>
          ) : (
            <Box sx={{
              width: '92%', height: '92%',
              background: '#e0e0e0',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              clipPath: 'polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%)',
              fontSize: '2rem', fontWeight: 700, color: '#888',
            }}>
              {initials || <PersonOutlineIcon sx={{ fontSize: 48 }}/>}
            </Box>
          )}
        </Box>

        {/* Info */}
        <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', justifyContent: 'space-around' }}>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6, mb: 0.2 }}>
              <PersonOutlineIcon sx={{ fontSize: 14, color: neonGreen }}/>
              <Typography sx={{ fontSize: '0.6rem', color: '#999', letterSpacing: '0.08em' }}>NOMBRE</Typography>
            </Box>
            <Typography sx={{ fontSize: '0.85rem', fontWeight: 700, color: '#fff', lineHeight: 1.15 }}>
              {t.nombre}
            </Typography>
            <Typography sx={{ fontSize: '0.85rem', fontWeight: 700, color: '#fff', lineHeight: 1.15 }}>
              {t.apellido}
            </Typography>
          </Box>

          <Box sx={{ borderTop: `0.5px solid ${neonGreen}30`, pt: 0.6 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6, mb: 0.2 }}>
              <VerifiedUserIcon sx={{ fontSize: 14, color: neonGreen }}/>
              <Typography sx={{ fontSize: '0.6rem', color: '#999', letterSpacing: '0.08em' }}>ESTADO</Typography>
            </Box>
            <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, color: neonGreen, letterSpacing: '0.02em' }}>
              {fmtVigencia}
            </Typography>
          </Box>

          <Box sx={{ borderTop: `0.5px solid ${neonGreen}30`, pt: 0.6 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6, mb: 0.2 }}>
              <BadgeIcon sx={{ fontSize: 14, color: neonGreen }}/>
              <Typography sx={{ fontSize: '0.6rem', color: '#999', letterSpacing: '0.08em' }}>ID SOCIO</Typography>
            </Box>
            <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, color: '#fff', fontFamily: 'monospace' }}>
              {t.tipoDocumento || 'CC'} {t.documento}
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Banda de iconos: valores */}
      <Box sx={{
        mt: 0.5, mx: 1.5, p: 1.2, borderRadius: 1.5,
        background: 'rgba(255,255,255,0.03)',
        border: `0.5px solid ${neonGreen}25`,
        display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 0.5,
        position: 'relative', zIndex: 2,
      }}>
        <ValorIcon icono="🤝" titulo="CONECTAMOS" sub="empresas y personas" color={neonGreen}/>
        <ValorIcon icono="🌱" titulo="FORTALECEMOS" sub="alianzas que generan valor" color={neonGreen}/>
        <ValorIcon icono="📈" titulo="CRECEMOS" sub="en red y con propósito" color={neonGreen}/>
        <ValorIcon icono="🌍" titulo="JUNTOS" sub="generamos impacto real" color={neonGreen}/>
      </Box>

      {/* QR + verificación + sello */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, px: 2, py: 1.5, position: 'relative', zIndex: 2 }}>
        <Box sx={{ background: '#fff', p: 0.3, borderRadius: 0.6, lineHeight: 0, boxShadow: `0 0 8px ${neonGreen}40` }}>
          <QRCodeSVG value={urlValidacion} size={62} level="M" includeMargin={false}/>
        </Box>
        <Box sx={{ flex: 1, minWidth: 0, fontSize: '0.62rem', color: '#999' }}>
          <Typography sx={{ fontSize: '0.6rem', color: '#aaa', letterSpacing: '0.08em', mb: 0.3 }}>
            VERIFICA TU AFILIACIÓN
          </Typography>
          <Typography sx={{ fontSize: '0.62rem', color: neonGreen, fontFamily: 'monospace', wordBreak: 'break-all' }}>
            🌐 {urlCorta}
          </Typography>
          <Typography sx={{ fontSize: '0.58rem', color: '#aaa', letterSpacing: '0.05em', mt: 0.5 }}>
            🛡️ ID VERIFICACIÓN
          </Typography>
          <Typography sx={{ fontSize: '0.62rem', color: '#fff', fontFamily: 'monospace' }}>
            {carnet.codigo}
          </Typography>
        </Box>

        {/* Sello circular */}
        <Box sx={{
          width: 64, height: 64, flexShrink: 0,
          borderRadius: '50%',
          border: `1.5px solid ${neonGreen}`,
          background: 'radial-gradient(circle, rgba(123,255,26,0.05), transparent)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          position: 'relative',
        }}>
          <Box sx={{
            width: 50, height: 50, borderRadius: '50%',
            border: `0.5px dashed ${neonGreen}80`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: '0.5rem', color: neonGreen, fontWeight: 700,
            textAlign: 'center', lineHeight: 1.1, letterSpacing: '0.02em',
            background: 'rgba(0,0,0,0.4)',
          }}>
            ALIANZA<br/>SOLIDARIA<br/>RSE
          </Box>
        </Box>
      </Box>

      {/* Footer */}
      <Box sx={{
        background: 'rgba(255,255,255,0.04)',
        py: 1, px: 2,
        textAlign: 'center',
        borderTop: `0.5px solid ${neonGreen}20`,
        position: 'relative', zIndex: 2,
      }}>
        <Typography sx={{
          fontSize: '0.6rem', color: '#aaa', letterSpacing: '0.18em', mb: 0.2,
        }}>
          UNIDOS · SOLIDARIOS · PRODUCTIVOS
        </Typography>
        <Typography sx={{
          fontSize: '0.65rem', color: neonGreen, fontWeight: 700, letterSpacing: '0.05em',
        }}>
          JUNTOS TRANSFORMAMOS EL CAMPO
        </Typography>
      </Box>
    </Box>
  );
}

function ValorIcon({ icono, titulo, sub, color }) {
  return (
    <Box sx={{ textAlign: 'center' }}>
      <Box sx={{ fontSize: '1.1rem', mb: 0.2, lineHeight: 1 }}>{icono}</Box>
      <Typography sx={{ fontSize: '0.5rem', fontWeight: 700, color, letterSpacing: '0.04em' }}>
        {titulo}
      </Typography>
      <Typography sx={{ fontSize: '0.46rem', color: '#888', lineHeight: 1.1, mt: 0.1 }}>
        {sub}
      </Typography>
    </Box>
  );
}

// ─────────────────────────────────────────────────────────────────────
// VERSIÓN 3: ORIGINAL — usa carnetCapellaniasBase.jpeg como fondo y
// superpone foto, datos y QR del titular real. Las cajas oscuras
// tapan los datos del afiliado de muestra que vienen en la imagen base.
// ─────────────────────────────────────────────────────────────────────
export function CarnetCardOriginal({ carnet, fotoUrl, cardRef }) {
  if (!carnet) return null;

  const t = carnet.titular || {};
  const photo = fotoUrl || t.fotoUrl || null;
  const initials = ((t.nombre?.[0] || '') + (t.apellido?.[0] || '')).toUpperCase();

  const urlValidacion = carnet.urlValidacion
    || `https://solidarityallianceofchaplains.org/v/${carnet.codigo}`;
  const urlCorta = urlValidacion
    .replace(/^https?:\/\//, '')
    .replace(/\/v\/[A-Za-z0-9]+/, '/v/...');

  const estadoLbl = carnet.estado === 'ACTIVO' ? 'AFILIADO ACTIVO' : carnet.estado;

  // Aspect ratio de la imagen base: 896:1344 = 2:3
  const CARD_WIDTH = 340;
  const CARD_HEIGHT = Math.round(CARD_WIDTH * 1344 / 896);

  const pct = (n) => `${n}%`;

  return (
    <Box
      ref={cardRef}
      sx={{
        position: 'relative',
        width: CARD_WIDTH,
        height: CARD_HEIGHT,
        backgroundImage: `url(${carnetCapellaniasBase})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        borderRadius: '14px',
        overflow: 'hidden',
        boxShadow: '0 12px 40px rgba(0,0,0,0.5)',
        fontFamily: '"Outfit", "Inter", sans-serif',
        color: '#fff',
      }}
    >
      {/* ── Foto del titular: forma exacta del escudo extraída pixel-perfect
          de la imagen original. Va dentro del borde verde del escudo. ── */}
      <Box sx={{
        position: 'absolute',
        left: pct(10.94), top: pct(32.74),
        width: pct(29.24), height: pct(28.27),
        WebkitMaskImage: `url(${escudoMask})`,
        WebkitMaskSize: '100% 100%',
        WebkitMaskRepeat: 'no-repeat',
        WebkitMaskPosition: 'center',
        maskImage: `url(${escudoMask})`,
        maskSize: '100% 100%',
        maskRepeat: 'no-repeat',
        maskPosition: 'center',
        backgroundImage: photo ? `url(${photo})` : 'none',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundColor: '#e0e0e0',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        {!photo && (
          <Box sx={{
            fontSize: '1.6rem', fontWeight: 700, color: '#666',
          }}>
            {initials || '—'}
          </Box>
        )}
      </Box>

      {/* ── Nombre (2 líneas) — letra pequeña para evitar montones ── */}
      <Box sx={{
        position: 'absolute',
        left: pct(56), top: pct(36.5),
        width: pct(43), height: pct(8.5),
        display: 'flex', flexDirection: 'column', justifyContent: 'center',
      }}>
        <Typography sx={{
          fontSize: '0.78rem', fontWeight: 700, color: '#fff',
          lineHeight: 1.15, letterSpacing: '0.005em',
        }}>
          {t.nombre}
        </Typography>
        <Typography sx={{
          fontSize: '0.78rem', fontWeight: 700, color: '#fff',
          lineHeight: 1.15, letterSpacing: '0.005em',
        }}>
          {t.apellido}
        </Typography>
      </Box>

      {/* ── Estado — letra pequeña ── */}
      <Box sx={{
        position: 'absolute',
        left: pct(56), top: pct(47.5),
        width: pct(43), height: pct(4.5),
        display: 'flex', alignItems: 'center',
      }}>
        <Typography sx={{
          fontSize: '0.75rem', fontWeight: 700, color: '#7BFF1A',
          letterSpacing: '0.02em',
        }}>
          {estadoLbl}
        </Typography>
      </Box>

      {/* ── ID Socio — letra pequeña ── */}
      <Box sx={{
        position: 'absolute',
        left: pct(56), top: pct(55.5),
        width: pct(43), height: pct(4.5),
        display: 'flex', alignItems: 'center',
      }}>
        <Typography sx={{
          fontSize: '0.75rem', fontWeight: 700, color: '#fff',
          fontFamily: 'monospace', letterSpacing: '0.02em',
        }}>
          {t.tipoDocumento || 'CC'} {t.documento}
        </Typography>
      </Box>

      {/* ── QR del titular (ajustado +5px abajo, +5px derecha) ── */}
      <Box sx={{
        position: 'absolute',
        left: pct(8), top: pct(79.5),
        width: pct(19), height: pct(13),
        background: '#fff',
        padding: '3px',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        borderRadius: 1,
      }}>
        <QRCodeSVG value={urlValidacion} size={56} level="M" includeMargin={false}
          style={{ width: '100%', height: '100%' }}/>
      </Box>

      {/* ── URL verificación — muy pequeña, ancho limitado para no tocar el sello ── */}
      <Box sx={{
        position: 'absolute',
        left: pct(32.5), top: pct(83),
        width: pct(33), height: pct(5),
        display: 'flex', alignItems: 'center',
      }}>
        <Typography sx={{
          fontSize: '0.5rem', color: '#7BFF1A',
          fontFamily: 'monospace', lineHeight: 1.2,
          wordBreak: 'break-all',
        }}>
          {urlCorta}
        </Typography>
      </Box>

      {/* ── Código de verificación ── */}
      <Box sx={{
        position: 'absolute',
        left: pct(32.5), top: pct(91),
        width: pct(33), height: pct(3.5),
        display: 'flex', alignItems: 'center',
      }}>
        <Typography sx={{
          fontSize: '0.7rem', color: '#fff',
          fontFamily: 'monospace', fontWeight: 600,
        }}>
          {carnet.codigo}
        </Typography>
      </Box>
    </Box>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Utilidades de descarga e impresión
// ─────────────────────────────────────────────────────────────────────
export async function descargarCarnetPng(cardEl, codigo) {
  try {
    const html2canvasMod = await import('html2canvas');
    const html2canvas = html2canvasMod.default || html2canvasMod;
    const canvas = await html2canvas(cardEl, {
      scale: 2, backgroundColor: null, useCORS: true, allowTaint: true,
    });
    const link = document.createElement('a');
    link.download = `carnet_${codigo}.png`;
    link.href = canvas.toDataURL('image/png');
    link.click();
  } catch (err) {
    imprimirCarnet(cardEl, codigo);
  }
}

export function imprimirCarnet(cardEl, codigo) {
  const printWindow = window.open('', '_blank', 'width=600,height=900');
  if (!printWindow) return;
  const html = cardEl?.outerHTML || '';
  printWindow.document.write(`
    <html>
      <head>
        <title>Carnet ${codigo}</title>
        <style>
          body { margin: 0; padding: 30px; font-family: 'Outfit', sans-serif; background: #f5f5f5; }
          @media print {
            body { background: #fff; padding: 0; }
            @page { size: 85.6mm 130mm; margin: 0; }
          }
        </style>
      </head>
      <body>${html}</body>
    </html>
  `);
  printWindow.document.close();
  setTimeout(() => { printWindow.print(); printWindow.close(); }, 250);
}

// ─────────────────────────────────────────────────────────────────────
// CarnetCardConSelector — envuelve ambas versiones cuando es CAPELLANES.
// Para FENAGRO renderiza solo la versión clara.
// Expone el ref del nodo visible al padre (vía onCardRef) para que pueda
// descargar/imprimir el que está actualmente visible.
// ─────────────────────────────────────────────────────────────────────
export function CarnetCardConSelector({ carnet, fotoUrl, onCardRef }) {
  const claroRef    = useRef(null);
  const oscuroRef   = useRef(null);
  const originalRef = useRef(null);
  const [version, setVersion] = useState('claro');

  const esCapellanes = carnet?.cooperativa?.siglas === 'CAPELLANES';

  // Cuando cambia el version o el carnet, propagar el ref correcto al padre
  React.useEffect(() => {
    if (!onCardRef) return;
    const ref = version === 'oscuro'   ? oscuroRef.current
              : version === 'original' ? originalRef.current
              :                          claroRef.current;
    onCardRef(ref);
  }, [version, carnet, onCardRef]);

  if (!carnet) return null;

  return (
    <Box sx={{ display:'flex', flexDirection:'column', alignItems:'center', gap:2 }}>
      {esCapellanes && (
        <ToggleButtonGroup
          value={version}
          exclusive
          size="small"
          onChange={(_, v) => v && setVersion(v)}
          sx={{
            '& .MuiToggleButton-root': {
              textTransform: 'none', fontWeight: 600, fontSize: '0.78rem', px: 2,
            },
            '& .MuiToggleButton-root.Mui-selected': {
              background: '#1e3a5f', color: '#fff',
              '&:hover': { background: '#2c4f7c' },
            },
          }}
        >
          <ToggleButton value="claro">Versión clara</ToggleButton>
          <ToggleButton value="oscuro">Versión oscura</ToggleButton>
          <ToggleButton value="original">Versión original</ToggleButton>
        </ToggleButtonGroup>
      )}

      <Box sx={{ display: version === 'claro' ? 'block' : 'none' }}>
        <CarnetCard carnet={carnet} fotoUrl={fotoUrl} cardRef={claroRef}/>
      </Box>
      <Box sx={{ display: version === 'oscuro' ? 'block' : 'none' }}>
        <CarnetCardOscuro carnet={carnet} fotoUrl={fotoUrl} cardRef={oscuroRef}/>
      </Box>
      <Box sx={{ display: version === 'original' ? 'block' : 'none' }}>
        <CarnetCardOriginal carnet={carnet} fotoUrl={fotoUrl} cardRef={originalRef}/>
      </Box>
    </Box>
  );
}

// ─────────────────────────────────────────────────────────────────────
// DialogCarnet — wrapper con dialog para mostrar en pop-up
// ─────────────────────────────────────────────────────────────────────
export default function DialogCarnet({ open, onClose, carnet, fotoUrl }) {
  const cardRefRef = useRef(null);
  if (!carnet) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth
      PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden', background: '#f5f5f5' } }}>

      <Box sx={{ p: 1.5, display: 'flex', justifyContent: 'flex-end', background: '#fafafa', borderBottom: '1px solid #e0e0e0' }}>
        <IconButton onClick={onClose} size="small"><CloseIcon/></IconButton>
      </Box>

      <DialogContent sx={{ background: '#f5f5f5', display: 'flex', justifyContent: 'center', py: 3 }}>
        <CarnetCardConSelector
          carnet={carnet}
          fotoUrl={fotoUrl}
          onCardRef={(el) => { cardRefRef.current = el; }}
        />
      </DialogContent>

      <DialogActions sx={{ p: 2, background: '#fafafa', borderTop: '1px solid #e0e0e0', gap: 1 }}>
        <Tooltip title="Descargar como PNG">
          <Button onClick={() => descargarCarnetPng(cardRefRef.current, carnet.codigo)}
            startIcon={<DownloadIcon/>} variant="outlined"
            sx={{ color: '#2e7d32', borderColor: '#2e7d32' }}>
            Descargar
          </Button>
        </Tooltip>
        <Tooltip title="Imprimir">
          <Button onClick={() => imprimirCarnet(cardRefRef.current, carnet.codigo)}
            startIcon={<PrintIcon/>} variant="contained"
            sx={{ background: 'linear-gradient(135deg, #2e7d32, #43a047)', fontWeight: 700 }}>
            Imprimir
          </Button>
        </Tooltip>
      </DialogActions>
    </Dialog>
  );
}
